import { Coins } from "lucide-react";

const fmtMoney = (n, currency = "USD") =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(n);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
};

export default function DividendInfoCard({ quote, loading }) {
  if (loading) {
    return (
      <div
        data-testid="dividend-info-card"
        className="h-full border border-zinc-800 bg-[#121214] p-6 sm:p-8 flex flex-col gap-3"
      >
        <div className="h-3 w-32 bg-zinc-800 animate-pulse" />
        <div className="h-10 w-28 bg-zinc-800 animate-pulse" />
        <div className="h-4 w-40 bg-zinc-800 animate-pulse" />
        <div className="h-4 w-32 bg-zinc-800 animate-pulse" />
      </div>
    );
  }
  if (!quote) return null;

  const d = quote.dividend || {};
  const yieldPct = d.fwd_yield_pct ?? 0;
  const hasDiv = (d.frequency && d.frequency !== "Inactive") || yieldPct > 0;
  const exSource = d.next_ex_date_source;

  return (
    <div
      data-testid="dividend-info-card"
      className="h-full border border-zinc-800 bg-[#121214] p-6 sm:p-8 flex flex-col"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-label flex items-center gap-2">
          <Coins size={12} className="text-[#FFD60A]" /> Dividend Snapshot
        </div>
        {hasDiv && (
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#FFD60A]/80 border border-[#FFD60A]/30 px-2 py-0.5">
            Income
          </span>
        )}
      </div>

      <div>
        <div className="text-label">Forward Yield</div>
        <div
          className="font-mono text-4xl font-bold tracking-tight text-[#FFD60A] mt-1"
          data-testid="fwd-yield-value"
        >
          {hasDiv ? `${yieldPct.toFixed(2)}%` : "—"}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-zinc-900">
        <div>
          <div className="text-label">Frequency</div>
          <div className="font-mono text-sm mt-1" data-testid="div-frequency">
            {d.frequency || "—"}
          </div>
        </div>
        <div>
          <div className="text-label">Last Amount</div>
          <div className="font-mono text-sm mt-1" data-testid="div-amount">
            {fmtMoney(d.amount, quote.currency)}
          </div>
        </div>
        <div>
          <div className="text-label">Annualized</div>
          <div className="font-mono text-sm mt-1">{fmtMoney(d.annualized, quote.currency)}</div>
        </div>
        <div>
          <div className="text-label flex items-center gap-1">
            Next Ex-Date
            {exSource === "estimated" && (
              <span className="text-[8px] font-mono uppercase tracking-widest text-zinc-500 border border-zinc-700 px-1">
                est
              </span>
            )}
          </div>
          <div className="font-mono text-sm mt-1" data-testid="div-ex-date">
            {fmtDate(d.next_ex_date)}
          </div>
        </div>
      </div>
    </div>
  );
}
